<template>
  <div class="d-discover-feed">
    <header class="no-border-radius">
      <div class="wrapper">
        <div class="grid-5">
          <div class="grid-item-3">
            <div class="header-content hidden-until-md">
              <h1>Discover something new to read</h1>
              <p class="large">
                Book recommendations from real people, not robots.
                Uncover future bestsellers by voting for your favorites.
              </p>
            </div>
            <div class="visible-until-sm space-top-3em space-bottom-2em">
              <h1>Discover something new&nbsp;to&nbsp;read</h1>
              <p class="large">
                Books recommended by real people, not robots.
                Upvote your favorites.
              </p>
            </div>
          </div>
        </div>
        <span class="map" />
      </div>
    </header>

    <section
      ref="discoverSection"
      class="section-grey section-thin"
    >
      <div class="wrapper">
        <div class="discover-container">
          <DDiscoverFeedSearch />

          <div
            class="books-container panel panel-overflow loadable"
            :class="{ 'loading': bookSearch.isSearching }"
          >
            <DuiLoadingIndicator
              v-if="bookSearch.isSearching"
              size="lg"
            />

            <div class="loaded-content">
              <!-- Grouped entries -->
              <div
                v-if="!(storeDisplayedSearchTags && storeDisplayedSearchTags.length)"
                class="books-list"
              >
                <!-- Feed header -->
                <div
                  class="feed-header grid-flex grid-no-response"
                  :class="{ 'border-bottom': !groupedResults.length }"
                >
                  <div class="grid-item">
                    <p class="large">
                      <span v-if="selectedGenreName">
                        <strong>{{ selectedGenreName }}</strong>
                        <span v-if="(groupedResults.length && isDefaultTimescale)">
                          published {{ groupedResults[0].relativeDate }}
                        </span>
                        <span v-if="(groupedResults.length && !isDefaultTimescale)">
                          sorted by {{ groupedResults[0].relativeDate }}
                        </span>
                      </span>

                      <span v-if="(!selectedGenreName && groupedResults.length && isDefaultTimescale)">
                        Published {{ groupedResults[0].relativeDate }}
                      </span>

                      <span v-if="(!selectedGenreName && groupedResults.length && !isDefaultTimescale)">
                        Sorted by {{ groupedResults[0].relativeDate }}
                      </span>
                    </p>
                  </div>
                  <div class="grid-item-shrink">
                    <nav class="menu">
                      <ul>
                        <li>
                          <a
                            class="menu-entry"
                            :class="{ 'active': !sortBy }"
                            @click="setSortBy(null)"
                          >
                            Popular
                          </a>
                        </li>
                        <li>
                          <a
                            class="menu-entry"
                            :class="{ 'active': sortBy }"
                            @click="setSortBy('launch_date')"
                          >
                            Recent
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
                <!-- END Feed header -->

                <div v-if="groupedResults.length">
                  <DDiscoverFeedEntriesGroup
                    v-for="(group, index) in groupedResults"
                    :key="index"
                    :group="group"
                    :is-expanded="(index === 0)"
                    :is-user-signed-in="isUserSignedIn"
                    :number-of-entries="10"
                    :show-relative-date="(index !== 0)"
                  />
                </div>
              </div>
              <!-- END Grouped entries -->

              <!-- Tags and no. of results -->
              <div
                v-if="(storeDisplayedSearchTags && storeDisplayedSearchTags.length)"
                class="space-top-2em"
              >
                <DSearchTags store-search-prefix="DiscoverFeed/BooksSearch" />
                <DBooksSearchNumberResults
                  class="space-bottom-none padding-bottom-3em"
                  :total-count="bookSearch.meta.total_count"
                />
              </div>
              <!-- END Tags and no. of results -->

              <!-- No results -->
              <div
                v-if="!bookSearch.meta.total_count"
                class="no-results-with-vessels"
              >
                <img
                  v-for="n in bookVesselNum"
                  :key="n"
                  alt="no-results"
                  :src="bookVesselPath"
                  title="No results"
                >
              </div>
              <!-- END No results -->

              <div
                v-if="(storeDisplayedSearchTags && storeDisplayedSearchTags.length)"
                class="books-list"
              >
                <DDiscoverFeedEntries
                  :entries="bookSearch.results"
                  :is-user-signed-in="isUserSignedIn"
                />
              </div>

              <DuiPagination
                v-if="(!bookSearch.isSearching && bookSearch.meta.total_pages > 1 && bookSearch.results.length)"
                class="border-top padding-top-2em"
                :total-pages="bookSearch.meta.total_pages"
                :value="bookSearch.meta.current_page"
                @input="setPageTag"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Vue from 'vue';

import DBooksSearchNumberResults from '@d-components/d-books-search/d-books-search-number-results/d-books-search-number-results.vue';
import DSearchTags from '@d-components/d-search-tags/d-search-tags.vue';
import BookEntriesHelper from '@d-helpers/book-entries-helper';
import DateGroupingHelper from '@d-helpers/date-grouping-helper';
import discoverSearchService from '@d-services/books-search-service/discover-search-service';
import { SCROLL_OPTIONS } from '@d-shared/config';
import { getErrorMessage } from '@d-shared/utils/errors';

import DDiscoverFeedEntries from './d-discover-feed-entries/d-discover-feed-entries.vue';
import DDiscoverFeedEntriesGroup from './d-discover-feed-entries-group/d-discover-feed-entries-group.vue';
import DDiscoverFeedSearch from './d-discover-feed-search/d-discover-feed-search.vue';

export default {
  components: {
    DBooksSearchNumberResults,
    DDiscoverFeedEntries,
    DDiscoverFeedEntriesGroup,
    DDiscoverFeedSearch,
    DSearchTags,
  },
  props: {
    bookVesselPath: {
      type: String,
      default: '',
    },
    isUserSignedIn: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    bookSearch: {
      isSearching: true,
      meta: {},
      results: [],
    },
    bookVesselNum: 3,
    sortBy: null,
  }),
  computed: {
    groupedResults() {
      if (!this.bookSearch.results || !this.bookSearch.results.length) {
        return [];
      }

      const timescaleTag = this.storeSearchTags.find(t => t.key === 'timescale');

      const groups = timescaleTag ?
        this.getGroupedEntriesByTimescale(timescaleTag) :
        DateGroupingHelper.getGroupsByWeek(this.bookSearch.results, 'launch_date', 'll');

      if (!timescaleTag) {
        BookEntriesHelper.sortGroupEntriesByProperty(groups, 'score');
      }

      return groups;
    },
    selectedGenreName() {
      const genre = this.$store.getters['DiscoverFeed/get-selected-genre'];
      if (!genre || !genre.value) {
        return null;
      }

      return genre.text;
    },
    storeDisplayedSearchTags() {
      return this.$store.getters['DiscoverFeed/BooksSearch/get-displayed-search-tags'];
    },
    storeSearchTags() {
      return this.$store.getters['DiscoverFeed/BooksSearch/get-all-search-tags'];
    },
    isDefaultTimescale() {
      const timescaleTag = this.storeSearchTags.find(t => t.key === 'timescale');
      return !timescaleTag;
    },
  },
  watch: {
    storeDisplayedSearchTags: function() {
      this.updateSortByFromTag();
    },
  },
  mounted() {
    this.loadBooks();
  },
  methods: {
    addSearchTag(searchTag) {
      this.$store.dispatch('DiscoverFeed/BooksSearch/addSearchTagAndSearch', searchTag);
    },

    getGroupedEntriesByTimescale(timescaleTag) {
      const relativeDate = timescaleTag.value === 'all' ? 'all time' : `this ${timescaleTag.value}`;
      return [
        {
          entries: this.bookSearch.results,
          relativeDate: relativeDate,
        },
      ];
    },

    clearPagination() {
      const paginationTag = { key: 'page' };
      this.removeSearchTag(paginationTag);
    },

    getSortByTag(sortByValue) {
      return {
        hidden: true,
        key: 'sortBy',
        order: true,
        value: sortByValue || null,
      };
    },

    async loadBooks() {
      try {
        this.bookSearch = await discoverSearchService.getBooks();
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }
    },

    removeSearchTag(searchTag) {
      this.$store.dispatch('DiscoverFeed/BooksSearch/removeSearchTag', searchTag);
    },

    setPageTag(pageNumber) {
      const tag = {
        hidden: true,
        key: 'page',
        page: true,
        text: `Page ${ pageNumber }`,
        value: (pageNumber !== 1) ? pageNumber : null,
      };

      this.$scrollTo(this.$refs.discoverSection, SCROLL_OPTIONS);

      if (!tag.value) {
        this.clearPagination();
        return;
      }

      this.addSearchTag(tag);
    },

    setSortBy(sortByValue) {
      this.$set(this, 'sortBy', sortByValue);

      const sortByTag = this.getSortByTag(sortByValue);

      if (sortByTag.value === null) {
        this.removeSearchTag(sortByTag);
        this.clearPagination();
        return;
      }

      this.addSearchTag(sortByTag);
    },

    updateSortByFromTag() {
      const sortByTag = this.storeSearchTags.find(t => t.key === 'sortBy');
      if (!sortByTag) {
        this.$set(this, 'sortBy', null);
        return;
      }

      if (this.sortBy !== sortByTag.value) {
        this.$set(this, 'sortBy', sortByTag.value);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-discover-feed {
  background-color: getColor($white);
  border-top-left-radius: $border-radius-lg;
  border-top-right-radius: $border-radius-lg;

  header {
    background: transparent;
    overflow: hidden;
    position: relative;

    .map {
      background: url('../../../../assets/map-animated.svg') no-repeat;
      background-position: bottom right;
      background-size: 100%;
      bottom: -12px;
      display: none;
      height: calc(100% + 48px);
      position: absolute;
      right: -44px;
      width: 48%;

      @include screenMoreThan(sm) {
        display: block;
        min-height: 100%;
      }

      @include screenMoreThan(md) {
        height: calc(100% + 48px);
      }
    }
  }

  .discover-container {
    display: grid;
    grid-row-gap: $_3em;
    grid-template-areas:
      "d-discover-feed-search"
      "books-container";

    .books-filter {
      grid-area: books-filter;
      min-width: 0;
    }

    .books-container {
      grid-area: books-container;
      padding-top: $_1em;

      .feed-header {
        p {
          margin: $_2em $_2em 0 0;

          @include screenMoreThan(xs) {
            margin-bottom: $_2em;
          }
        }
      }
    }

    @include screenMoreThan(sm) {
      grid-column-gap: $_8em;
      grid-row-gap: 0;
      grid-template-columns: repeat(3, 1fr);
      grid-template-areas:
        "d-discover-feed-search books-container books-container";
    }
  }
}
</style>
